import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase/supabase';

const PrinterContext = createContext();

export const usePrinter = () => {
  const context = useContext(PrinterContext);
  if (!context) {
    throw new Error('usePrinter must be used within a PrinterProvider');
  }
  return context;
};

export const PrinterProvider = ({ children }) => {
  const [printers, setPrinters] = useState([]);
  
  useEffect(() => {
    loadPrinters();
  }, []);

  const loadPrinters = async () => {
    try {
      const { data, error } = await supabase
        .from('printer_settings')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      const formattedPrinters = (data || []).map(printer => ({
        ...printer,
        ipAddress: printer.ip_address,
        isConnected: printer.is_connected,
        lastTestAt: printer.last_test_at,
      }));
      setPrinters(formattedPrinters);
    } catch (error) {
      console.error('Error loading printers:', error);
    }
  };

  const addPrinter = async (printer) => {
    try {
      await supabase
        .from('printer_settings')
        .insert([
          {
            name: printer.name,
            type: printer.type,
            ip_address: printer.ipAddress,
            port: printer.port,
            description: printer.description,
            is_connected: false,
          },
        ])
        .select()
        .single();
      
      await loadPrinters();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to add printer' };
    }
  };

  const updatePrinter = async (printerId, updatedPrinter) => {
    try {
      await supabase
        .from('printer_settings')
        .update({
          name: updatedPrinter.name,
          type: updatedPrinter.type,
          ip_address: updatedPrinter.ipAddress,
          port: updatedPrinter.port,
          description: updatedPrinter.description,
          is_connected: updatedPrinter.isConnected,
          last_test_at: updatedPrinter.lastTestAt,
          updated_at: new Date().toISOString(),
        })
        .eq('id', printerId);
      
      await loadPrinters();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to update printer' };
    }
  };

  const deletePrinter = async (printerId) => {
    try {
      await supabase.from('printer_settings').delete().eq('id', printerId);
      await loadPrinters();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to delete printer' };
    }
  };

  const testPrinter = async (printerId) => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await updatePrinter(printerId, { isConnected: true, lastTestAt: new Date().toISOString() });
      return { success: true, message: 'Printer test successful' };
    } catch (error) {
      await updatePrinter(printerId, { isConnected: false });
      return { success: false, message: 'Printer test failed' };
    }
  };
  
 const generateOrderReceipt = (order) => {
    const customerInfo = order.customerInfo || order.customer_info || {};
    const orderType = customerInfo.orderType || order.order_type || 'pickup';

    const formatPickupTime = () => {
      if (customerInfo.pickupTime && customerInfo.pickupTime !== 'asap') {
        const pickupDate = new Date(customerInfo.pickupTime);
        return pickupDate.toLocaleString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
      return 'ASAP (15-20 minutes)';
    };
    
    const itemsList = order.items.map(item => {
        let itemString = `\n${item.quantity}x ${item.name}`;
        const priceString = `$${(item.totalPrice || item.total_price).toFixed(2)}`;
        const padding = 30 - (itemString.length + priceString.length);
        itemString += ' '.repeat(Math.max(0, padding)) + priceString;

        const customizations = item.customizations || {};
        if (customizations.bread) itemString += `\n  - Bread: ${customizations.bread}`;
        if (customizations.protein) itemString += `\n  - Protein: ${customizations.protein}`;
        if (customizations.toppings?.length) itemString += `\n  - Toppings: ${customizations.toppings.join(', ')}`;
        if (customizations.extras?.length) itemString += `\n  - Extras: ${customizations.extras.join(', ')}`;
        return itemString;
    }).join('');

    const receipt = `
==============================
LA VIE SAIGON
==============================
@@ORDER_NUMBER_START@@
ORDER #${order.orderNumber || order.order_number}
@@ORDER_NUMBER_END@@
Date: ${new Date(order.createdAt || order.created_at).toLocaleString()}
Customer: ${customerInfo.name}
Phone: ${customerInfo.phone}
Order Type: ${orderType.toUpperCase()}
${orderType === 'pickup' ? 'Pickup' : 'Delivery'} Time: ${formatPickupTime()}
------------------------------${itemsList}
------------------------------
Total: $${order.total.toFixed(2)}
==============================
${
  (customerInfo.specialInstructions)
    ? `Special Instructions:
${customerInfo.specialInstructions}
==============================
`
    : ''
}
Thank you for your order!
==============================
`.trim();
    return receipt;
  };

  const printOrder = async (order, printerIds = []) => {
    try {
      const targetPrinters =
        printerIds.length > 0
          ? printers.filter((p) => printerIds.includes(p.id) && p.isConnected)
          : printers.filter((p) => p.isConnected);

      if (targetPrinters.length === 0) {
        try {
          const printContent = generateOrderReceipt(order);
          const iframe = document.createElement('iframe');
          iframe.style.position = 'absolute';
          iframe.style.left = '-9999px';
          iframe.style.width = '1px';
          iframe.style.height = '1px';
          document.body.appendChild(iframe);

          const doc = iframe.contentWindow.document;
          doc.open();
          doc.write(`
            <html>
              <head>
                <title>Receipt</title>
                <style>
                  body {font-family: 'Courier New', monospace; font-size: 12px; margin: 0; padding: 20px; line-height: 1.4;}
                  pre {white-space: pre-wrap; margin: 0;}
                  @media print { body {margin: 0; padding: 10px;} }
                </style>
              </head>
              <body>
          `);

          const orderNumberRegex = /@@ORDER_NUMBER_START@@\s*(.*?)\s*@@ORDER_NUMBER_END@@/s;
          const match = printContent.match(orderNumberRegex);

          if (match) {
            const orderNumberText = match[1];
            const orderNumberValue = orderNumberText.replace('ORDER #', '').trim();
            const [before, after] = printContent.split(match[0]);

            doc.write(`<pre>${before}</pre>`);
            doc.write(`<div style="font-size: 48px; font-weight: bold; text-align: center; margin: 10px 0;">#${orderNumberValue}</div>`);
            doc.write(`<pre>${after}</pre>`);
          } else {
            doc.write(`<pre>${printContent}</pre>`);
          }

          doc.write(`</body></html>`);
          doc.close();

          setTimeout(() => {
            try {
              iframe.contentWindow.print();
              setTimeout(() => {
                document.body.removeChild(iframe);
              }, 1000);
            } catch (e) {
              console.log('Silent print failed, but order was processed');
              document.body.removeChild(iframe);
            }
          }, 500);

          return { success: true, message: 'Receipt auto-printed', printedTo: ['System Printer'] };
        } catch (e) {
          console.log('Auto-print failed, but order was processed successfully');
          return { success: true, message: 'Order processed (print unavailable)', printedTo: [] };
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 500));
      generateOrderReceipt(order);
      return {
        success: true,
        message: `Order printed to ${targetPrinters.length} printer(s)`,
        printedTo: targetPrinters.map((p) => p.name),
      };
    } catch (error) {
      return { success: false, message: 'Print failed: ' + error.message };
    }
  };

  return (
    <PrinterContext.Provider
      value={{
        printers,
        addPrinter,
        updatePrinter,
        deletePrinter,
        testPrinter,
        printOrder,
        generateOrderReceipt,
      }}
    >
      {children}
    </PrinterContext.Provider>
  );
};